import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { Icon } from '../components/Icons.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api/client.js';
import { SocialAccounts } from './Misc.jsx';

const BILLING_USAGE_METRICS = [
  { key: 'publish_ready_posts_monthly', label: 'Publish-ready posts', short: 'Posts', icon: 'sparkles', tone: 'violet' },
  { key: 'premium_visuals_monthly', label: 'Premium AI visuals', short: 'Visuals', icon: 'image', tone: 'rose' },
  { key: 'content_plan_regenerations_monthly', label: 'Content Plan regenerations', short: 'Plan refreshes', icon: 'refresh', tone: 'blue' },
  { key: 'social_accounts_total', label: 'Social accounts', short: 'Socials', icon: 'plug', tone: 'green' },
  { key: 'brand_profiles_total', label: 'Brands', short: 'Brands', icon: 'user', tone: 'amber' },
  { key: 'assistant_turns_monthly', label: 'Assistant fair-use turns', short: 'Assistant', icon: 'msg', tone: 'slate' },
];

const NOTIFICATION_PREFERENCE_ROWS = [
  ['post_published', 'Post published'],
  ['ai_generated_drafts', 'AI generated drafts'],
  ['token_expiring', 'Connection/token expiry'],
  ['weekly_digest', 'Weekly digest'],
  ['product_updates', 'Product updates'],
];

const FEATURE_LABELS = {
  brand_profile: 'Brand profile',
  ai_generation: 'AI generation',
  image_generation: 'Image generation',
  carousel_generation: 'Carousel generation',
  scheduler: 'Scheduler',
  autopilot: 'Autopilot',
  approval_flow: 'Approval flow',
  social_accounts: 'Social accounts',
  publishing: 'Publishing',
  mcp: 'MCP',
  analytics: 'Analytics',
  admin_debug: 'Admin/debug',
};

const CHECKOUT_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    priceQuarterly: '$300',
    monthlyEquivalent: '$100/mo',
    desc: 'First serious plan for one brand that needs daily publish-ready content.',
    highlights: ['150 publish-ready posts/mo', '75 premium AI visuals/mo', '10 social accounts', 'Assistant included'],
    bestFor: '5 posts/day with visuals when they matter',
    posts: 150,
    visuals: 75,
    brands: 1,
    socials: 10,
    cta: 'Start Starter',
    popular: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    priceQuarterly: '$600',
    monthlyEquivalent: '$200/mo',
    desc: 'Higher-volume content engine for stronger testing and more channels.',
    highlights: ['300 publish-ready posts/mo', '150 premium AI visuals/mo', '3 brands', 'Priority assistant ceiling'],
    bestFor: '10 posts/day, more campaigns, deeper tests',
    posts: 300,
    visuals: 150,
    brands: 3,
    socials: 20,
    cta: 'Upgrade to Growth',
  },
  {
    id: 'pro',
    name: 'Pro',
    priceQuarterly: '$900+',
    monthlyEquivalent: '$300+/mo',
    desc: 'Large-volume plan for teams, agencies, and multi-brand operations.',
    highlights: ['750 publish-ready posts/mo', '350 premium AI visuals/mo', '10 brands', 'High support ceiling'],
    bestFor: 'Multi-channel publishing at scale',
    posts: 750,
    visuals: 350,
    brands: 10,
    socials: 50,
    cta: 'Start Pro',
  },
];

const BILLING_ADD_ONS = [
  { id: 'posts_50', title: '+50 posts', price: '$29', desc: 'For extra campaigns, seasonal pushes, or more daily drafts.', metric: 'publish_ready_posts_monthly' },
  { id: 'visuals_25', title: '+25 visuals', price: '$39', desc: 'Unlock more premium image generation without changing plan.', metric: 'premium_visuals_monthly' },
  { id: 'visuals_100', title: '+100 visuals', price: '$129', desc: 'Best for visual-heavy months and product launches.', metric: 'premium_visuals_monthly' },
];

const CONTENT_CADENCES = [
  { id: 'steady', label: 'Steady', posts: 90, visuals: 30, note: '3 posts/day, visuals for key ideas' },
  { id: 'daily', label: 'Daily Growth', posts: 150, visuals: 75, note: '5 posts/day, Pilot Growth sweet spot' },
  { id: 'aggressive', label: 'Aggressive', posts: 300, visuals: 150, note: '10 posts/day, Growth Plus territory' },
];



export function Billing() {
  const [tab, setTab] = useState('overview');
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interval] = useState('quarterly');
  const [cadence, setCadence] = useState('daily');
  const [busyAction, setBusyAction] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadBilling() {
      try {
        setLoading(true);
        setError(null);
        const response = await api.billing.getEntitlements();
        if (!cancelled) {
          setEntitlements(response);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load billing');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadBilling();
    return () => {
      cancelled = true;
    };
  }, []);

  async function startCheckout(plan) {
    try {
      setBusyAction(`checkout:${plan}`);
      setError(null);
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const result = await api.billing.createCheckoutSession({
        plan,
        interval,
        successUrl: origin ? `${origin}/billing?checkout=success` : null,
        cancelUrl: origin ? `${origin}/billing?checkout=cancelled` : null,
      });
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl);
        return;
      }
      setError('Stripe checkout did not return a redirect URL.');
    } catch (err) {
      setError(err.message || 'Unable to start checkout');
    } finally {
      setBusyAction(null);
    }
  }

  async function openPortal() {
    if (!canOpenPortal(entitlements)) {
      setError('Stripe portal is available after checkout creates a customer.');
      return;
    }

    try {
      setBusyAction('portal');
      setError(null);
      const returnUrl = typeof window !== 'undefined' ? window.location.href : null;
      const result = await api.billing.createPortalSession({ returnUrl });
      if (result.portalUrl) {
        window.location.assign(result.portalUrl);
        return;
      }
      setError('Stripe portal did not return a redirect URL.');
    } catch (err) {
      setError(err.message || 'Unable to open billing portal');
    } finally {
      setBusyAction(null);
    }
  }

  const portalAvailable = canOpenPortal(entitlements);

  return (
    <div className="page col ds-page" style={{ gap: 16 }}>
      <div className="row between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {entitlements?.plan && <span className="chip chip-active">{formatPlan(entitlements.plan)} plan</span>}
          {entitlements?.billingStatus && <span className="chip">{formatStatus(entitlements.billingStatus)}</span>}
          <button className="btn btn-secondary btn-sm" onClick={openPortal} disabled={!portalAvailable || busyAction === 'portal'}>
            <Icon name="creditCard" size={12} /> {busyAction === 'portal' ? 'Opening...' : portalAvailable ? 'Manage in Stripe' : 'Portal after checkout'}
          </button>
        </div>
        <div className="dt-tabs">
          {['overview', 'plans', 'usage', 'payments'].map(item => (
            <button key={item} className={'dt-tab ' + (tab === item ? 'is-active' : '')} onClick={() => setTab(item)}>{formatPlanTab(item)}</button>
          ))}
        </div>
      </div>

      {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}

      {loading && <BillingLoading />}
      {!loading && !entitlements && !error && <BillingEmpty />}
      {!loading && entitlements && tab === 'overview' && <BillingOverview entitlements={entitlements} cadence={cadence} setCadence={setCadence} onPortal={openPortal} portalBusy={busyAction === 'portal'} portalAvailable={portalAvailable} onViewPlans={() => setTab('plans')} />}
      {!loading && entitlements && tab === 'usage' && <BillingUsage usage={entitlements.usage || {}} />}
      {!loading && entitlements && tab === 'plans' && <BillingPlans currentPlan={entitlements.plan} interval={interval} onCheckout={startCheckout} busyAction={busyAction} />}
      {!loading && entitlements && tab === 'payments' && <BillingPayments onPortal={openPortal} portalBusy={busyAction === 'portal'} portalAvailable={portalAvailable} />}
    </div>
  );
}

function BillingOverview({ entitlements, cadence, setCadence, onPortal, portalBusy, portalAvailable, onViewPlans }) {
  const rows = metricRows(entitlements.usage).filter(row => row.limit !== 0);
  const headlineRows = rows.filter(row => ['publish_ready_posts_monthly', 'premium_visuals_monthly', 'social_accounts_total', 'brand_profiles_total'].includes(row.key));
  const currentPlan = planFor(entitlements.plan);
  const selectedCadence = CONTENT_CADENCES.find(item => item.id === cadence) || CONTENT_CADENCES[1];
  const recommendedPlan = recommendPlan(selectedCadence);
  const renewLabel = entitlements.currentPeriodEnd ? formatDate(entitlements.currentPeriodEnd) : 'after checkout';

  return (
    <div className="billing-v2 col" style={{ gap: 16 }}>
      <section className="billing-hero card ds-card-pad">
        <div className="billing-hero-copy">
          <span className="settings-section-label">Plan & Usage</span>
          <h2>{currentPlan?.name || formatPlan(entitlements.plan)} keeps your content engine capped, profitable, and predictable.</h2>
          <p>Buy concrete outputs: publish-ready posts, premium visuals, brands, and social accounts. Assistant stays included and guides the workflow.</p>
          <div className="billing-hero-actions">
            <button className="btn btn-primary" onClick={onViewPlans}>Compare plans</button>
            <button className="btn btn-secondary" onClick={onPortal} disabled={!portalAvailable || portalBusy}>
              <Icon name="creditCard" size={13} /> {portalBusy ? 'Opening...' : portalAvailable ? 'Manage billing' : 'Portal after checkout'}
            </button>
          </div>
        </div>
        <div className="billing-current-card">
          <span className="chip chip-active">{formatStatus(entitlements.billingStatus)}</span>
          <strong>{currentPlan?.name || formatPlan(entitlements.plan)}</strong>
          <span>{currentPlan?.priceQuarterly || 'Free'} {currentPlan ? 'per quarter' : ''}</span>
          <small>Renews {renewLabel}</small>
        </div>
      </section>

      <div className="billing-quota-grid">
        {headlineRows.map(row => <QuotaCard key={row.key} row={row} />)}
      </div>

      <section className="billing-simulator card ds-card-pad">
        <div className="row between" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div>
            <span className="settings-section-label">Interactive fit check</span>
            <h3 className="h3" style={{ margin: '4px 0' }}>Pick your monthly cadence</h3>
            <p className="caption" style={{ margin: 0 }}>We recommend the lowest plan that safely covers posts and visuals.</p>
          </div>
          <div className="billing-recommendation-pill">Recommended: {recommendedPlan.name}</div>
        </div>
        <div className="billing-cadence-grid">
          {CONTENT_CADENCES.map(item => (
            <button key={item.id} type="button" className={'billing-cadence-card ' + (item.id === selectedCadence.id ? 'is-active' : '')} onClick={() => setCadence(item.id)}>
              <strong>{item.label}</strong>
              <span>{item.posts} posts + {item.visuals} visuals/month</span>
              <small>{item.note}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="billing-commerce card ds-card-pad">
        <div>
          <span className="settings-section-label">When you hit a limit</span>
          <h3 className="h3" style={{ margin: '4px 0' }}>No dead-end quota errors.</h3>
          <p className="caption" style={{ margin: 0 }}>If visuals run out, text posts, editing, scheduling, publishing, and Assistant still work. The blocked action becomes an upgrade/add-on choice.</p>
        </div>
        <div className="billing-addon-grid">
          {BILLING_ADD_ONS.map(addOn => <AddOnCard key={addOn.id} addOn={addOn} />)}
        </div>
      </section>
    </div>
  );
}

function QuotaCard({ row }) {
  return (
    <article className={'billing-quota-card tone-' + row.tone}>
      <div className="billing-quota-top">
        <span className="type-icon"><Icon name={row.icon} size={16} /></span>
        <span>{row.statusLabel}</span>
      </div>
      <strong>{formatLimit(row.remaining)}</strong>
      <small>{row.short} left of {formatLimit(row.limit)}</small>
      <div className="dt-progress"><div className="dt-progress-fill" style={{ width: `${row.percent}%` }} /></div>
      <div className="billing-quota-foot"><span>{row.used} used</span><span>{row.percent}%</span></div>
    </article>
  );
}

function BillingUsage({ usage }) {
  const rows = metricRows(usage);
  return (
    <div className="card billing-usage-table" style={{ padding: 0 }}>
      <div className="row between" style={{ padding: '18px 24px', borderBottom: '1px solid var(--border-soft)', gap: 12 }}>
        <div>
          <h3 className="h3">Usage by limit</h3>
          <p className="caption" style={{ margin: '4px 0 0' }}>Result quotas for the current billing month. Credits are internal, not the product.</p>
        </div>
      </div>
      {rows.length ? rows.map(row => (
        <div key={row.key} className="ds-usage-row billing-usage-row">
          <div className="type-icon" style={{ color: 'var(--p-300)' }}><Icon name={row.icon} size={16} /></div>
          <div className="col" style={{ flex: 1, gap: 6, minWidth: 0 }}>
            <div style={{ fontWeight: 650 }}>{row.label}</div>
            <div className="dt-progress"><div className="dt-progress-fill" style={{ width: `${row.percent}%` }} /></div>
          </div>
          <div className="caption hide-mobile">{row.statusLabel}</div>
          <div style={{ fontWeight: 700 }}>{row.used} / {formatLimit(row.limit)}</div>
        </div>
      )) : (
        <div className="ds-usage-row"><div className="caption">No usage counters returned yet.</div></div>
      )}
    </div>
  );
}

function BillingPlans({ currentPlan, interval, onCheckout, busyAction }) {
  const canonicalCurrent = canonicalPlanId(currentPlan);
  return (
    <div className="col" style={{ gap: 16 }}>
      <div className="billing-plan-header card ds-card-pad">
        <div>
          <span className="settings-section-label">Quarterly plans</span>
          <h3 className="h3" style={{ marginBottom: 4 }}>Choose output volume, not credits.</h3>
          <p className="caption" style={{ margin: 0 }}>Quarterly upfront keeps support, AI cost, and customer commitment aligned.</p>
        </div>
        <span className="billing-recommendation-pill">Pilot Growth is default live plan</span>
      </div>
      <div className="ds-plan-grid billing-plan-grid">
        {CHECKOUT_PLANS.map(plan => (
          <PlanCard
            key={plan.id}
            plan={plan}
            current={canonicalCurrent === plan.id}
            interval={interval}
            busy={busyAction === `checkout:${plan.id}`}
            onCheckout={onCheckout}
          />
        ))}
        <div className="card ds-plan-card billing-plan-card custom">
          <span className="chip">Custom</span>
          <div className="h3">Agency / Team</div>
          <div className="billing-plan-price">Custom</div>
          <p className="caption">High-volume, invoiced, custom limits, and custom support expectations.</p>
          <div className="col" style={{ gap: 8, marginTop: 16 }}>
            {['Custom posts and visuals', 'Team or agency workflows', 'Invoice-based contract'].map(feature => (
              <div key={feature} className="billing-check"><Icon name="check" size={12} />{feature}</div>
            ))}
          </div>
          <button className="btn btn-secondary btn-block" style={{ marginTop: 18 }} disabled>Contact sales soon</button>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, current, interval, busy, onCheckout }) {
  return (
    <div className={'card ds-plan-card billing-plan-card ' + (current ? 'current' : '') + (plan.popular ? ' popular' : '')}>
      {(current || plan.popular) && <span className="chip chip-active ds-plan-badge">{current ? 'Current plan' : 'Recommended'}</span>}
      <div className="h3">{plan.name}</div>
      <p className="caption" style={{ margin: '10px 0 0' }}>{plan.desc}</p>
      <div className="billing-plan-price">{plan.priceQuarterly}<span>/quarter</span></div>
      <div className="caption">{plan.monthlyEquivalent} equivalent</div>
      <div className="billing-plan-output">
        <div><strong>{plan.posts}</strong><span>posts/mo</span></div>
        <div><strong>{plan.visuals}</strong><span>visuals/mo</span></div>
      </div>
      <div className="col" style={{ gap: 8, marginTop: 16 }}>{plan.highlights.map(feature => <div key={feature} className="billing-check"><Icon name="check" size={12} />{feature}</div>)}</div>
      <div className="billing-best-for">{plan.bestFor}</div>
      <button className={'btn btn-block ' + (current ? 'btn-primary' : 'btn-secondary')} style={{ marginTop: 18 }} disabled={current || busy} onClick={() => onCheckout(plan.id)}>
        {current ? 'Current plan' : busy ? 'Opening checkout...' : `${plan.cta} (${interval})`}
      </button>
    </div>
  );
}

function AddOnCard({ addOn }) {
  return (
    <article className="billing-addon-card">
      <strong>{addOn.title}</strong>
      <span>{addOn.price}</span>
      <small>{addOn.desc}</small>
    </article>
  );
}

function BillingPayments({ onPortal, portalBusy, portalAvailable }) {
  return (
    <div className="card ds-card-pad">
      <h3 className="h3" style={{ marginBottom: 8 }}>Payments and invoices</h3>
      <p className="caption" style={{ marginBottom: 18 }}>Invoice history is managed in the Stripe customer portal. Dathent only exposes the portal session endpoint here.</p>
      {!portalAvailable && <div className="auth-note" style={{ marginBottom: 14 }}>Start checkout first. The Stripe portal is available after a Stripe customer exists.</div>}
      <button className="btn btn-primary" onClick={onPortal} disabled={!portalAvailable || portalBusy}>
        <Icon name="creditCard" size={14} /> {portalBusy ? 'Opening...' : portalAvailable ? 'Open Stripe portal' : 'Portal after checkout'}
      </button>
    </div>
  );
}

function BillingLoading() {
  return (
    <div className="card ds-card-pad">
      <div className="row" style={{ gap: 10 }}>
        <Icon name="refresh" size={16} />
        <div>Loading billing entitlements...</div>
      </div>
    </div>
  );
}

function BillingEmpty() {
  return (
    <div className="card ds-card-pad">
      <h3 className="h3" style={{ marginBottom: 6 }}>Billing unavailable</h3>
      <p className="caption" style={{ margin: 0 }}>The billing service did not return an entitlement snapshot.</p>
    </div>
  );
}

function metricRows(usage = {}) {
  return BILLING_USAGE_METRICS.map(metric => {
    const snapshot = usage[metric.key] || legacyUsageSnapshot(metric.key, usage) || { used: 0, limit: 0, remaining: 0 };
    const used = Number(snapshot.used || 0);
    const limit = snapshot.limit === null ? null : Number(snapshot.limit || 0);
    const percent = limit === null ? 0 : limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const remaining = snapshot.remaining === null ? null : Number(snapshot.remaining || 0);
    return {
      ...metric,
      used,
      limit,
      remaining,
      percent,
      statusLabel: usageStatusLabel(percent, limit),
    };
  });
}

function legacyUsageSnapshot(key, usage) {
  if (key === 'publish_ready_posts_monthly') return usage.ai_generations_monthly || usage.published_posts_monthly || usage.scheduled_posts_monthly;
  if (key === 'premium_visuals_monthly') return usage.image_generations_monthly;
  return null;
}

function usageStatusLabel(percent, limit) {
  if (limit === null) return 'Unlimited';
  if (limit === 0) return 'Not included';
  if (percent >= 100) return 'Limit reached';
  if (percent >= 80) return 'Near limit';
  return 'Healthy';
}

function formatPlan(plan) {
  if (!plan) return 'Free';
  if (plan === 'starter') return 'Pilot Growth';
  if (plan === 'pro') return 'Growth Plus';
  return plan.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function formatPlanTab(tab) {
  if (tab === 'plans') return 'Plans';
  if (tab === 'usage') return 'Usage';
  if (tab === 'payments') return 'Payments';
  return 'Overview';
}

function formatStatus(status) {
  if (!status) return 'Unknown status';
  return status.replace(/_/g, ' ');
}

function formatFeature(feature) {
  return FEATURE_LABELS[feature] || formatPlan(feature);
}

function formatLimit(value) {
  return value === null ? 'Unlimited' : Number(value || 0).toLocaleString();
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function canonicalPlanId(plan) {
  // Map legacy aliases to current canonical IDs (kept for older billing rows).
  if (plan === 'pilot_growth') return 'starter';
  if (plan === 'growth_plus') return 'growth';
  if (plan === 'business') return 'pro';
  return plan || 'free';
}

function planFor(plan) {
  return CHECKOUT_PLANS.find(item => item.id === canonicalPlanId(plan)) || null;
}

function recommendPlan(cadence) {
  return CHECKOUT_PLANS.find(plan => plan.posts >= cadence.posts && plan.visuals >= cadence.visuals) || CHECKOUT_PLANS[CHECKOUT_PLANS.length - 1];
}

function canOpenPortal(entitlements) {
  return Boolean(entitlements && entitlements.billingStatus && entitlements.billingStatus !== 'free');
}


export function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const initialTab = (() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const valid = ['profile', 'security', 'billing', 'social', 'notifications', 'danger'];
    return valid.includes(tab) ? tab : 'profile';
  })();
  const [section, setSectionState] = useState(initialTab);
  const setSection = (id) => {
    setSectionState(id);
    const params = new URLSearchParams(location.search);
    params.set('tab', id);
    routerNavigate({ pathname: '/settings', search: `?${params.toString()}` }, { replace: true });
  };
  const [pwdOpen, setPwdOpen] = useState(false);
  const [mfaModal, setMfaModal] = useState(null);
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaError, setMfaError] = useState(null);
  const sections = [
    ['profile', 'Profile', 'user'],
    ['security', 'Security', 'lock'],
    ['billing', 'Plan & Usage', 'creditCard'],
    ['social', 'Social accounts', 'plug'],
    ['notifications', 'Notifications', 'bell'],
    ['danger', 'Danger', 'trash'],
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const valid = ['profile', 'security', 'billing', 'social', 'notifications', 'danger'];
    if (valid.includes(tab) && tab !== section) setSectionState(tab);
  }, [location.search]);
  const displayName = user?.name || user?.email || 'Dathent user';

  async function loadMfaStatus() {
    try {
      setMfaLoading(true);
      setMfaError(null);
      setMfaStatus(await api.auth.getMfaStatus());
    } catch (err) {
      setMfaError(err.message || 'Unable to load two-factor status');
    } finally {
      setMfaLoading(false);
    }
  }

  useEffect(() => {
    loadMfaStatus();
  }, []);

  return (
    <div className="page settings-shell-v2 settings-desktop-content desktop-page-content">
      <div className="settings-summary-grid settings-summary" aria-label="Account overview">
        <div className="settings-summary-card settings-summary-row"><span className="settings-summary-label">Workspace</span><strong className="settings-summary-value">{displayName}</strong></div>
        <div className="settings-summary-card settings-summary-row"><span className="settings-summary-label">Plan</span><strong className="settings-summary-value">Free</strong></div>
        <div className="settings-summary-card settings-summary-row"><span className="settings-summary-label">Post quota</span><strong className="settings-summary-value">Plan based</strong></div>
      </div>
      <div className="settings-layout">
        <nav className="settings-side-v2 settings-tabs-card" aria-label="Settings sections">
          {sections.map(([id, label, icon]) => (
            <button key={id} className={'settings-tab ' + (section === id ? 'is-active active' : '')} onClick={() => setSection(id)}>
              <Icon name={icon} size={16}/>
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="settings-main-v2 col">
        {section === 'profile' && <ProfileSettings user={user} />}
        {section === 'security' && <SecuritySettings mfaStatus={mfaStatus} mfaLoading={mfaLoading} mfaError={mfaError} onSetup={() => setMfaModal('setup')} onDisable={() => setMfaModal('disable')} onRegenerate={() => setMfaModal('regenerate')} setPwdOpen={setPwdOpen} />}
        {section === 'billing' && <Billing />}
        {section === 'social' && <SocialAccounts />}
        {section === 'notifications' && <NotificationSettings />}
        {section === 'danger' && <DangerSettings />}
        </div>
      </div>
      {pwdOpen && <PasswordModal onClose={() => setPwdOpen(false)} />}
      {mfaModal === 'setup' && <TwoFASetupModal onClose={() => setMfaModal(null)} onComplete={loadMfaStatus} />}
      {mfaModal === 'disable' && <TwoFADisableModal onClose={() => setMfaModal(null)} onComplete={loadMfaStatus} />}
      {mfaModal === 'regenerate' && <BackupCodesRegenerateModal onClose={() => setMfaModal(null)} onComplete={loadMfaStatus} />}
    </div>
  );
}

function ProfileSettings({ user }) {
  const displayName = user?.name || user?.email || 'Dathent user';
  const [fullName, setFullName] = useState(user?.name || '');
  const [status, setStatus] = useState('idle');
  const initials = displayName.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join('') || 'DU';
  const originalName = user?.name || '';
  const changed = fullName !== originalName;

  useEffect(() => {
    setFullName(user?.name || '');
    setStatus('idle');
  }, [user?.name]);

  function saveProfile() {
    if (!changed) return;
    setStatus('saving');
    window.setTimeout(() => setStatus('saved'), 450);
  }

  return (
    <div className="card ds-card-pad settings-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Profile</h3>
        <p className="settings-card-description">How you appear inside Dathent.</p>
      </div>
      <div className="profile-identity-block">
        <div className="avatar profile-avatar">{initials}</div>
        <div className="profile-identity-meta">
          <div className="profile-identity-name">{displayName}</div>
          <div className="profile-identity-email">{user?.email || 'No email connected'}</div>
        </div>
        <div className="profile-identity-actions">
          <button className="btn btn-secondary btn-sm" disabled>Upload photo</button>
          <button className="btn btn-ghost btn-sm" disabled>Remove</button>
        </div>
      </div>
      <div className="settings-section-label">Personal information</div>
      <div className="profile-form-grid">
        <div className="form-field">
          <label>Full name</label>
          <input className="input form-input" value={fullName} onChange={event => { setFullName(event.target.value); setStatus('idle'); }} />
        </div>
        <div className="form-field">
          <label>Email</label>
          <input className="input form-input" value={user?.email || ''} readOnly />
          <div className="helper">Email is managed by your login provider.</div>
        </div>
      </div>
      {status === 'saved' && <div className="auth-note settings-inline-state"><Icon name="checkCircle" size={14}/> Saved</div>}
      <div className="settings-card-footer">
        <button className="btn btn-secondary" disabled={!changed || status === 'saving'} onClick={() => { setFullName(originalName); setStatus('idle'); }}>Cancel</button>
        <button className="btn btn-primary" disabled={!changed || status === 'saving'} onClick={saveProfile}>{status === 'saving' ? 'Saving...' : 'Save changes'}</button>
      </div>
    </div>
  );
}

function SecuritySettings({ mfaStatus, mfaLoading, mfaError, onSetup, onDisable, onRegenerate, setPwdOpen }) {
  const enabled = !!mfaStatus?.totp?.enabled;
  const remainingCodes = mfaStatus?.backupCodes?.remaining ?? 0;
  return (
    <div className="card ds-card-pad settings-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Security</h3>
        <p className="settings-card-description">Manage password, sessions, and login methods.</p>
      </div>
      {mfaError && <div className="auth-error" style={{ marginBottom: 12 }}><Icon name="alert" size={14} /> {mfaError}</div>}
      <SettingRow title="Password" desc="Change password soon" action={<button className="btn btn-secondary" onClick={() => setPwdOpen(true)}>Change</button>} embedded />
      <SettingRow
        title="Authenticator app"
        desc={enabled ? `Enabled. ${remainingCodes} backup codes remaining.` : 'Use one-time codes for stronger security.'}
        badge={enabled ? 'Enabled' : null}
        action={<button className={'switch ' + (enabled ? 'on' : '')} disabled={mfaLoading} onClick={enabled ? onDisable : onSetup} aria-label={enabled ? 'Disable authenticator app' : 'Set up authenticator app'} />}
        embedded
      />
      {enabled && (
        <SettingRow
          title="Backup codes"
          desc="Generate new one-time recovery codes. Old unused codes will stop working."
          action={<button className="btn btn-secondary btn-sm" onClick={onRegenerate}>Regenerate</button>}
          embedded
        />
      )}
      <SettingRow title="Active sessions" desc="Coming soon" action={<button className="btn btn-secondary" disabled>Manage</button>} embedded />
    </div>
  );
}

function NotificationSettings() {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadPreferences() {
      try {
        setLoading(true);
        setError(null);
        const response = await api.notifications.getPreferences();
        if (!cancelled) setPreferences(response.preferences?.email || {});
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load notification preferences');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadPreferences();
    return () => {
      cancelled = true;
    };
  }, []);

  async function togglePreference(key) {
    const next = { ...(preferences || {}), [key]: !(preferences || {})[key] };
    const previous = preferences;
    setPreferences(next);
    setSavingKey(key);
    setError(null);
    setMessage(null);
    try {
      const response = await api.notifications.updatePreferences({ email: next });
      setPreferences(response.preferences?.email || next);
      setMessage('Notification preferences saved.');
    } catch (err) {
      setPreferences(previous);
      setError(err.message || 'Unable to save notification preferences');
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="card ds-card-pad settings-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Notifications</h3>
        <p className="settings-card-description">Choose which product updates you want to receive.</p>
      </div>
      {error && <div className="auth-error" style={{ marginBottom: 12 }}><Icon name="alert" size={14} /> {error}</div>}
      {message && <div className="auth-note" style={{ marginBottom: 12 }}><Icon name="checkCircle" size={14} /> {message}</div>}
      {loading ? <div className="caption">Loading notification preferences...</div> : NOTIFICATION_PREFERENCE_ROWS.map(([key, label]) => (
        <SettingRow
          key={key}
          title={label}
          desc="Email notification"
          action={<button className={'switch ' + (preferences?.[key] ? 'on' : '')} disabled={savingKey === key} onClick={() => togglePreference(key)} />}
          embedded
        />
      ))}
    </div>
  );
}

function DangerSettings() {
  return <div className="card ds-card-pad settings-card settings-card-danger"><div className="settings-card-header"><h3 className="settings-card-title">Danger zone</h3><p className="settings-card-description">Permanent account actions.</p></div><SettingRow title="Delete workspace" desc="Request permanent deletion of this workspace and its data." action={<button className="btn btn-secondary btn-sm" style={{ borderColor: 'rgba(244,107,107,0.4)', color: 'var(--red)' }}>Request deletion</button>} embedded /></div>;
}

function SettingRow({ title, desc, action, badge, embedded }) {
  const content = <div className="row between ds-setting-row"><div><div style={{ fontWeight: 600 }}>{title} {badge && <span className="chip chip-green" style={{ marginLeft: 6 }}>{badge}</span>}</div><div className="caption" style={{ marginTop: 4 }}>{desc}</div></div>{action}</div>;
  return embedded ? content : <div className="card ds-card-pad">{content}</div>;
}

function PasswordModal({ onClose }) {
  return <div className="modal-scrim" onClick={onClose}><div className="modal" onClick={event => event.stopPropagation()}><h3 className="h2" style={{ marginBottom: 16 }}>Change password</h3><label className="label">Current password</label><input className="input" type="password" /><label className="label" style={{ marginTop: 14 }}>New password</label><input className="input" type="password" /><div className="row" style={{ gap: 8, marginTop: 20 }}><button className="btn btn-secondary btn-block" onClick={onClose}>Cancel</button><button className="btn btn-primary btn-block" onClick={onClose}>Update password</button></div></div></div>;
}

function TwoFASetupModal({ onClose, onComplete }) {
  const [setup, setSetup] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function startSetup() {
      try {
        setLoading(true);
        setError(null);
        const data = await api.auth.startMfaSetup();
        if (cancelled) return;
        setSetup(data);
        const qr = await QRCode.toDataURL(data.otpauthUrl, { width: 196, margin: 1, color: { dark: '#111111', light: '#ffffff' } });
        if (!cancelled) setQrDataUrl(qr);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to start authenticator setup');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    startSetup();
    return () => { cancelled = true; };
  }, []);

  function requestClose() {
    if (backupCodes && !savedCodes && !window.confirm('Backup codes are shown only once. Close without confirming you saved them?')) return;
    onClose();
  }

  async function verify() {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter a valid 6-digit code.');
      return;
    }
    try {
      setWorking(true);
      setError(null);
      const result = await api.auth.verifyMfaSetup(code);
      setBackupCodes(result.backupCodes || []);
      await onComplete?.();
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  async function copyText(value) {
    await navigator.clipboard?.writeText(value);
  }

  function downloadBackupCodes() {
    const blob = new Blob([backupCodes.join('\n') + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dathent-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="modal-scrim" onClick={requestClose}>
      <div className="modal mfa-modal" onClick={event => event.stopPropagation()}>
        {!backupCodes ? (
          <>
            <h3 className="h2" style={{ marginBottom: 8 }}>Set up Authenticator app</h3>
            <p className="caption" style={{ marginBottom: 20 }}>Scan this QR code with Google Authenticator, 1Password, Authy, Microsoft Authenticator, or another authenticator app.</p>
            {loading ? <div className="mfa-qr mfa-qr-loading">Generating QR...</div> : qrDataUrl ? <div className="mfa-qr"><img src={qrDataUrl} alt="Authenticator app QR code" /></div> : null}
            {setup?.manualKey && (
              <div className="mfa-manual-key">
                <div>
                  <div className="settings-section-label" style={{ margin: 0 }}>Can’t scan the QR code?</div>
                  <p className="caption" style={{ margin: '4px 0 10px' }}>Enter this setup key manually in your authenticator app.</p>
                </div>
                <code>{setup.manualKey}</code>
                <button className="btn btn-secondary btn-sm" type="button" onClick={() => copyText(setup.manualKey)}>Copy setup key</button>
              </div>
            )}
            <label className="label" style={{ marginTop: 16 }}>6-digit code</label>
            <input className="input" inputMode="numeric" pattern="[0-9]*" placeholder="000000" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} maxLength={6} autoComplete="one-time-code" />
            {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}
            <div className="row" style={{ gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary btn-block" type="button" onClick={requestClose}>Cancel</button>
              <button className="btn btn-primary btn-block" type="button" disabled={working || loading || code.length !== 6} onClick={verify}>{working ? 'Verifying...' : 'Verify & enable'}</button>
            </div>
          </>
        ) : (
          <>
            <h3 className="h2" style={{ marginBottom: 8 }}>Save your backup codes</h3>
            <p className="caption" style={{ marginBottom: 16 }}>Store these codes in a safe place. Each code can be used once if you lose access to your authenticator app.</p>
            <div className="mfa-backup-grid">{backupCodes.map(codeValue => <code key={codeValue}>{codeValue}</code>)}</div>
            <div className="row" style={{ gap: 8, marginTop: 14 }}>
              <button className="btn btn-secondary btn-block" type="button" onClick={() => copyText(backupCodes.join('\n'))}>Copy all codes</button>
              <button className="btn btn-secondary btn-block" type="button" onClick={downloadBackupCodes}>Download codes</button>
            </div>
            <label className="row" style={{ gap: 8, marginTop: 18, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={savedCodes} onChange={event => setSavedCodes(event.target.checked)} style={{ accentColor: 'var(--p-500)', width: 16, height: 16, marginTop: 2 }} />
              <span className="caption">I have saved these codes.</span>
            </label>
            <button className="btn btn-primary btn-block" type="button" disabled={!savedCodes} onClick={onClose} style={{ marginTop: 18 }}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

function TwoFADisableModal({ onClose, onComplete }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  async function disable() {
    try {
      setWorking(true);
      setError(null);
      await api.auth.disableMfa(password, code);
      await onComplete?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Unable to disable authenticator app');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal mfa-modal" onClick={event => event.stopPropagation()}>
        <h3 className="h2" style={{ marginBottom: 8 }}>Disable Authenticator app?</h3>
        <p className="caption" style={{ marginBottom: 20 }}>This will reduce the security of your account. Confirm your password and enter a 6-digit code to continue.</p>
        <label className="label">Current password</label>
        <input className="input" type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" />
        <label className="label" style={{ marginTop: 14 }}>6-digit authenticator code</label>
        <input className="input" inputMode="numeric" pattern="[0-9]*" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} autoComplete="one-time-code" />
        {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}
        <div className="row" style={{ gap: 8, marginTop: 20 }}>
          <button className="btn btn-secondary btn-block" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-block" type="button" disabled={working || !password || code.length !== 6} onClick={disable}>{working ? 'Disabling...' : 'Disable 2FA'}</button>
        </div>
      </div>
    </div>
  );
}

function BackupCodesRegenerateModal({ onClose, onComplete }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  async function regenerate() {
    try {
      setWorking(true);
      setError(null);
      const result = await api.auth.regenerateMfaBackupCodes(password, code);
      setBackupCodes(result.backupCodes || []);
      await onComplete?.();
    } catch (err) {
      setError(err.message || 'Unable to regenerate backup codes');
    } finally {
      setWorking(false);
    }
  }

  async function copyCodes() {
    await navigator.clipboard?.writeText((backupCodes || []).join('\n'));
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal mfa-modal" onClick={event => event.stopPropagation()}>
        {!backupCodes ? (
          <>
            <h3 className="h2" style={{ marginBottom: 8 }}>Regenerate backup codes</h3>
            <p className="caption" style={{ marginBottom: 20 }}>Old unused backup codes will stop working. Confirm your password and authenticator code.</p>
            <label className="label">Current password</label>
            <input className="input" type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" />
            <label className="label" style={{ marginTop: 14 }}>6-digit authenticator code</label>
            <input className="input" inputMode="numeric" pattern="[0-9]*" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6} autoComplete="one-time-code" />
            {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}
            <div className="row" style={{ gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary btn-block" type="button" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary btn-block" type="button" disabled={working || !password || code.length !== 6} onClick={regenerate}>{working ? 'Generating...' : 'Regenerate'}</button>
            </div>
          </>
        ) : (
          <>
            <h3 className="h2" style={{ marginBottom: 8 }}>Save your new backup codes</h3>
            <p className="caption" style={{ marginBottom: 16 }}>These codes are shown only once. Store them somewhere safe.</p>
            <div className="mfa-backup-grid">{backupCodes.map(codeValue => <code key={codeValue}>{codeValue}</code>)}</div>
            <button className="btn btn-secondary btn-block" type="button" onClick={copyCodes} style={{ marginTop: 14 }}>Copy all codes</button>
            <label className="row" style={{ gap: 8, marginTop: 18, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={savedCodes} onChange={event => setSavedCodes(event.target.checked)} style={{ accentColor: 'var(--p-500)', width: 16, height: 16, marginTop: 2 }} />
              <span className="caption">I have saved these codes.</span>
            </label>
            <button className="btn btn-primary btn-block" type="button" disabled={!savedCodes} onClick={onClose} style={{ marginTop: 18 }}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}
